import { baseSplitApi as api } from "./baseApi";

const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    getBackOfficeListCourses: build.query<
      GetBackOfficeListCoursesApiResponse,
      GetBackOfficeListCoursesApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/bo/courses`,
        params: {
          page: queryArg.page,
          size: queryArg.size,
          keyword: queryArg.keyword,
          sort: queryArg.sort,
        },
      }),
    }),
    createCourse: build.mutation<CreateCourseApiResponse, CreateCourseApiArg>({
      query: (queryArg) => ({
        url: `/v1/bo/courses`,
        method: "POST",
        body: queryArg.courseUpdateRequest,
      }),
    }),
    getBackOfficeGetCourse: build.query<
      GetBackOfficeGetCourseApiResponse,
      GetBackOfficeGetCourseApiArg
    >({
      query: (queryArg) => ({ url: `/v1/bo/courses/${queryArg.id}` }),
    }),
    updateCourse: build.mutation<UpdateCourseApiResponse, UpdateCourseApiArg>({
      query: (queryArg) => ({
        url: `/v1/bo/courses/${queryArg.id}`,
        method: "PUT",
        body: queryArg.courseUpdateRequest,
      }),
    }),
    deleteCourseById: build.mutation<
      DeleteCourseByIdApiResponse,
      DeleteCourseByIdApiArg
    >({
      query: (queryArg) => ({
        url: `/v1/bo/courses/${queryArg.id}`,
        method: "DELETE",
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as bigsystherApi };
export type GetBackOfficeListCoursesApiResponse =
  /** status 200 OK */ BackOfficeCoursePage;
export type GetBackOfficeListCoursesApiArg = {
  /** page */
  page?: number;
  /** size */
  size?: number;
  /** keyword to search */
  keyword?: string;
  /** Format is sort=distance:desc,name:asc. Supported keys: createdAt, updatedAt */
  sort?: string[];
};
export type CreateCourseApiResponse = /** status 200 OK */ SuccessResponse201;
export type CreateCourseApiArg = {
  /** Course update */
  courseUpdateRequest: CourseUpdateRequest;
};
export type GetBackOfficeGetCourseApiResponse =
  /** status 200 OK */ EntityCourse;
export type GetBackOfficeGetCourseApiArg = {
  /** uuid */
  id: string;
};
export type UpdateCourseApiResponse = /** status 200 OK */ SuccessResponse200;
export type UpdateCourseApiArg = {
  /** uuid */
  id: string;
  /** Course update */
  courseUpdateRequest: CourseUpdateRequest;
};
export type DeleteCourseByIdApiResponse =
  /** status 200 OK */ SuccessResponse200;
export type DeleteCourseByIdApiArg = {
  /** uuid */
  id: string;
};
export type CookieResponse = {
  domain?: string;
  httpOnly?: boolean;
  maxAge?: number;
  name?: string;
  path?: string;
  secure?: boolean;
  value?: string;
};
export type AudioResponse = {
  cookies?: CookieResponse[];
  url?: string;
};
export type AudioMediaResponse = {
  content?: AudioResponse;
  id?: string;
};
export type ImageResponse = {
  type?: string;
  url?: string;
};
export type ImageMediaResponse = {
  content?: ImageResponse[];
  id?: string;
};
export type EntityMediaType = "image" | "video" | "audio";
export type VideoResponse = {
  cookies?: CookieResponse[];
  thumbnail?: ImageResponse[];
  url?: string;
};
export type VideoMediaResponse = {
  content?: VideoResponse;
  id?: string;
};
export type EntityMedia = {
  audio?: AudioMediaResponse;
  createdAt?: string;
  id?: string;
  image?: ImageMediaResponse;
  key?: string;
  type?: EntityMediaType;
  updatedAt?: string;
  video?: VideoMediaResponse;
};
export type EntityLanguage = "en" | "de" | "de";
export type EntityLecturerTranslation = {
  description?: string;
  id?: string;
  language?: EntityLanguage;
};
export type EntityLecturer = {
  contactEmail?: string;
  createdAt?: string;
  fullName?: string;
  id?: string;
  lecturerImage?: EntityMedia;
  translations?: EntityLecturerTranslation[];
  updatedAt?: string;
};
export type EntityLessonContentType =
  | "lct-theory"
  | "lct-experiment"
  | "lct-reminder"
  | "lct-scale";
export type EntityLessonContentStatus =
  | "lcs-active"
  | "lcs-preview"
  | "lcs-draft";
export type EntityLessonContentTranslation = {
  id?: string;
  language?: EntityLanguage;
  name?: string;
  noteMetaData?: string;
  scaleMetaData?: string;
  text?: string;
};
export type EntityLessonContent = {
  createdAt?: string;
  id?: string;
  lessonContentType?: EntityLessonContentType;
  media?: EntityMedia;
  order?: number;
  status?: EntityLessonContentStatus;
  translations?: EntityLessonContentTranslation[];
  updatedAt?: string;
};
export type EntityLessonStatus = "ls-active" | "ls-preview" | "ls-draft";
export type EntityLessonTranslation = {
  id?: string;
  language?: EntityLanguage;
  name?: string;
  text?: string;
};
export type EntityLesson = {
  contents?: EntityLessonContent[];
  createdAt?: string;
  id?: string;
  isFree?: boolean;
  order?: number;
  status?: EntityLessonStatus;
  titleImage?: EntityMedia;
  translations?: EntityLessonTranslation[];
  updatedAt?: string;
};
export type EntityCourseStatus = "cs-active" | "cs-preview" | "cs-draft";
export type EntityCourseImage = {
  createdAt?: string;
  id?: string;
  media?: EntityMedia;
  order?: number;
  updatedAt?: string;
};
export type EntityCourseTranslation = {
  description?: string;
  goalDescription?: string;
  id?: string;
  language?: EntityLanguage;
  name?: string;
  shortDescription?: string;
  structure?: string;
  targetGroup?: string;
};
export type EntityCourse = {
  createdAt?: string;
  id?: string;
  lecturer?: EntityLecturer;
  lessons?: EntityLesson[];
  status?: EntityCourseStatus;
  titleImages?: EntityCourseImage[];
  translations?: EntityCourseTranslation[];
  updatedAt?: string;
};
export type BackOfficeCoursePage = {
  count?: number;
  items?: EntityCourse[];
  last?: boolean;
  page?: number;
  size?: number;
};
export type ValidationError = {
  field?: string;
  message?: string;
};
export type ErrorResponse400 = {
  code?: number;
  data?: ValidationError[];
  message?: string;
};
export type ErrorResponse404 = {
  code?: number;
  data?: {
    [key: string]: string;
  }[];
  message?: string;
};
export type SuccessResponse201 = {
  id?: string;
};
export type CourseUpdateRequestTranslation = {
  /** Markdown supported */
  description: string;
  /** Markdown supported */
  goalDescription: string;
  language: EntityLanguage;
  name: string;
  shortDescription: string;
  /** Markdown supported */
  structure: string;
  /** Markdown supported */
  targetGroup: string;
};
export type CourseUpdateRequest = {
  lecturerID?: string;
  status: EntityCourseStatus;
  titleImageID?: string;
  translations: CourseUpdateRequestTranslation[];
};
export type SuccessResponse200 = {
  status?: string;
};
export const {
  useGetBackOfficeListCoursesQuery,
  useCreateCourseMutation,
  useGetBackOfficeGetCourseQuery,
  useUpdateCourseMutation,
  useDeleteCourseByIdMutation,
} = injectedRtkApi;
